import { useRef } from "react";
import "./TrendsPage.css";
import { fmt, fmtPct } from "./trendsFormat";

/**
 * TrendsMixedChart
 *
 * Dependency-free SVG port of the mixed bar+line Chart.js config shared by
 * the three old tabs (single-role / double-role / role-x-2-locations
 * components all built the same p-chart shape). Kept custom-SVG to stay
 * consistent with RangeBarsChart / DistributionChart / ComparisonChart
 * rather than pulling Chart.js in as a new dependency.
 *
 * Chart.js behaviors reproduced:
 *   - category x-axis, grouped bars on the LEFT (£) axis, beginAtZero with
 *     suggestedMax = maxSalary * 1.15 and "nice" ticks (same Heckbert
 *     nice-numbers approach ComparisonChart.jsx already uses);
 *   - lines on the RIGHT (%) axis with its own scale (negatives supported -
 *     pay change and inflation can both go below zero), grid drawn only for
 *     the left axis (grid.drawOnChartArea: false on y1);
 *   - per-point forecast recoloring on bars (amber fill/border arrays);
 *   - solid / dashed / dotted stroke per line series (the visual convention
 *     separating pay-change vs inflation series);
 *   - spanGaps: false - line paths BREAK at null points;
 *   - tension 0.3 curve (Catmull-Rom -> bezier, same as ComparisonChart);
 *   - index-mode tooltip: hovering a period column shows every series value
 *     for that period (em dash for nulls).
 *
 * Props:
 *   labels         string[]                      x categories (review periods)
 *   forecastFlags  boolean[]                     per-label is_forecasted_data
 *   bars           [{ label, fill, border, forecastFill, values }]
 *   lines          [{ label, color, dash, values, raw }]   dash: null | [5,4] | [2,2]
 *   height         number                        default 320
 *
 * `raw` (optional, per line): the un-coerced values used for TOOLTIP display
 * only. The old Chart.js charts plotted null pay-change as 0 (the line always
 * draws, anchored at zero - confirmed against the live old app) while the
 * tooltip showed an em dash for null. Callers therefore pass values with
 * nulls coerced to 0 for drawing, and the raw nulls here so the tooltip
 * still reads "—".
 */

function niceNum(range, round) {
  if (range <= 0) return 1;
  const exponent = Math.floor(Math.log10(range));
  const fraction = range / Math.pow(10, exponent);
  let niceFraction;
  if (round) {
    if (fraction < 1.5) niceFraction = 1;
    else if (fraction < 3) niceFraction = 2;
    else if (fraction < 7) niceFraction = 5;
    else niceFraction = 10;
  } else {
    if (fraction <= 1) niceFraction = 1;
    else if (fraction <= 2) niceFraction = 2;
    else if (fraction <= 5) niceFraction = 5;
    else niceFraction = 10;
  }
  return niceFraction * Math.pow(10, exponent);
}

/** beginAtZero left axis: 0 .. nice max. */
function niceScaleFromZero(maxValue, maxTicks = 7) {
  const safeMax = maxValue > 0 ? maxValue : 1;
  const step = niceNum(safeMax / (maxTicks - 1), true);
  const max = Math.ceil(safeMax / step) * step;
  return { min: 0, max, step };
}

/** Right (%) axis: nice range that may cross zero. */
function niceScaleAroundZero(minValue, maxValue, maxTicks = 7) {
  let lo = Math.min(minValue, 0);
  let hi = Math.max(maxValue, 0);
  if (hi === lo) hi = lo + 1;
  const step = niceNum((hi - lo) / (maxTicks - 1), true);
  return { min: Math.floor(lo / step) * step, max: Math.ceil(hi / step) * step, step };
}

/** Catmull-Rom -> cubic bezier (Chart.js tension ~0.3), same as ComparisonChart. */
function smoothPath(points) {
  if (points.length < 2) return points.length === 1 ? "" : "";
  if (points.length === 2) return `M${points[0][0]},${points[0][1]} L${points[1][0]},${points[1][1]}`;
  let d = `M${points[0][0]},${points[0][1]} `;
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[i === 0 ? 0 : i - 1];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[i + 2 < points.length ? i + 2 : i + 1];
    const cp1x = p1[0] + (p2[0] - p0[0]) / 6;
    const cp1y = p1[1] + (p2[1] - p0[1]) / 6;
    const cp2x = p2[0] - (p3[0] - p1[0]) / 6;
    const cp2y = p2[1] - (p3[1] - p1[1]) / 6;
    d += `C${cp1x.toFixed(1)},${cp1y.toFixed(1)} ${cp2x.toFixed(1)},${cp2y.toFixed(1)} ${p2[0].toFixed(1)},${p2[1].toFixed(1)} `;
  }
  return d;
}

/** spanGaps:false - split a series into runs of consecutive non-null points. */
function nonNullRuns(values, xAt, yAt) {
  const runs = [];
  let current = [];
  values.forEach((v, i) => {
    if (v == null) {
      if (current.length) runs.push(current);
      current = [];
    } else {
      current.push([xAt(i), yAt(v)]);
    }
  });
  if (current.length) runs.push(current);
  return runs;
}

const W = 1100;
const M = { top: 20, right: 74, bottom: 58, left: 84 };

export default function TrendsMixedChart({ labels = [], forecastFlags = [], bars = [], lines = [], height = 320 }) {
  const tooltipRef = useRef(null);
  const H = height + M.top + M.bottom;

  if (!labels.length) return null;

  const innerW = W - M.left - M.right;
  const innerH = H - M.top - M.bottom;

  const maxBar = Math.max(1, ...bars.flatMap((b) => b.values.map((v) => v ?? 0)));
  const left = niceScaleFromZero(maxBar * 1.15);
  const lineVals = lines.flatMap((l) => l.values.filter((v) => v != null));
  const right = niceScaleAroundZero(Math.min(0, ...lineVals), Math.max(0, ...lineVals));

  const slotW = innerW / labels.length;
  const xCenter = (i) => M.left + slotW * i + slotW / 2;
  const yLeft = (v) => M.top + innerH - ((v - left.min) / (left.max - left.min)) * innerH;
  const yRight = (v) => M.top + innerH - ((v - right.min) / (right.max - right.min)) * innerH;

  // Grouped bar geometry - Chart.js-ish: the group takes ~55% of the slot.
  const groupW = slotW * 0.55;
  const barW = groupW / Math.max(bars.length, 1);

  const leftTicks = [];
  for (let v = left.min; v <= left.max + 1e-9; v += left.step) leftTicks.push(v);
  const rightTicks = [];
  for (let v = right.min; v <= right.max + 1e-9; v += right.step) rightTicks.push(v);

  // Sparse x labels to avoid crowding, always keeping the last one.
  const labelEvery = Math.ceil(labels.length / 10);

  function showTip(e, idx) {
    const tt = tooltipRef.current;
    if (!tt) return;
    const rows = [
      ...bars.map((b) => ({ name: b.label, val: b.values[idx] == null ? "—" : fmt(b.values[idx]), sw: b.fill })),
      ...lines.map((l) => ({ name: l.label, val: fmtPct((l.raw ?? l.values)[idx]), sw: l.color })),
    ];
    tt.innerHTML =
      `<div class="tr-tt-title">${labels[idx]}${forecastFlags[idx] ? " · forecast" : ""}</div>` +
      rows
        .map(
          (r) =>
            `<div class="tr-tt-row"><span class="tr-tt-sw" style="background:${r.sw}"></span><span class="tr-tt-label">${r.name}</span><span class="tr-tt-val">${r.val}</span></div>`
        )
        .join("");
    tt.classList.add("on");
    const pad = 14;
    const vw = window.innerWidth, vh = window.innerHeight;
    let x = e.clientX + pad, y = e.clientY + pad;
    if (x + tt.offsetWidth > vw - 8) x = e.clientX - pad - tt.offsetWidth;
    if (y + tt.offsetHeight > vh - 8) y = e.clientY - pad - tt.offsetHeight;
    tt.style.left = x + "px";
    tt.style.top = y + "px";
  }
  function hideTip() {
    tooltipRef.current?.classList.remove("on");
  }

  return (
    <div className="tr-chart-wrap">
      <svg className="tr-chart-svg" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet" aria-label="Wages and inflation chart">
        {/* forecast column shading behind everything */}
        {labels.map((_, i) =>
          forecastFlags[i] ? (
            <rect key={`fc${i}`} x={M.left + slotW * i} y={M.top} width={slotW} height={innerH} className="tr-forecast-band" />
          ) : null
        )}

        {/* left-axis grid + labels */}
        {leftTicks.map((v) => (
          <g key={`lt${v}`}>
            <line className="tr-grid-line" x1={M.left} x2={M.left + innerW} y1={yLeft(v)} y2={yLeft(v)} />
            <text className="tr-axis-label" x={M.left - 10} y={yLeft(v) + 4} textAnchor="end">{`£${(v / 1000).toFixed(0)}k`}</text>
          </g>
        ))}
        {/* right-axis labels (no grid across the chart area - drawOnChartArea:false) */}
        {rightTicks.map((v) => (
          <text key={`rt${v}`} className="tr-axis-label" x={M.left + innerW + 10} y={yRight(v) + 4} textAnchor="start">{`${v}%`}</text>
        ))}
        <line className="tr-axis-line" x1={M.left} x2={M.left} y1={M.top} y2={M.top + innerH} />
        <line className="tr-axis-line" x1={M.left + innerW} x2={M.left + innerW} y1={M.top} y2={M.top + innerH} />
        <line className="tr-axis-line" x1={M.left} x2={M.left + innerW} y1={M.top + innerH} y2={M.top + innerH} />

        {/* bars */}
        {bars.map((b, bi) =>
          b.values.map((v, i) => {
            if (v == null) return null;
            const x = xCenter(i) - groupW / 2 + barW * bi;
            const y = yLeft(v);
            const isFc = !!forecastFlags[i];
            return (
              <rect
                key={`b${bi}-${i}`}
                x={x + 1}
                y={y}
                width={Math.max(barW - 2, 1)}
                height={Math.max(M.top + innerH - y, 0)}
                fill={isFc ? b.forecastFill : b.fill}
                stroke={isFc ? "#ffc107" : b.border}
                strokeWidth="1"
                rx="2"
              />
            );
          })
        )}

        {/* lines (broken at nulls - spanGaps: false) */}
        {lines.map((l, li) =>
          nonNullRuns(l.values, xCenter, yRight).map((run, ri) => (
            <path
              key={`l${li}-${ri}`}
              d={smoothPath(run)}
              fill="none"
              stroke={l.color}
              strokeWidth="2"
              strokeDasharray={l.dash ? l.dash.join(" ") : undefined}
            />
          ))
        )}
        {/* line points (white-filled, matching pointBackgroundColor: '#fff') */}
        {lines.map((l, li) =>
          l.values.map((v, i) =>
            v == null ? null : (
              <circle key={`p${li}-${i}`} cx={xCenter(i)} cy={yRight(v)} r="3.5" fill="#fff" stroke={l.color} strokeWidth="2" />
            )
          )
        )}

        {/* x labels */}
        {labels.map((lbl, i) =>
          i % labelEvery === 0 || i === labels.length - 1 ? (
            <text key={`xl${i}`} className="tr-axis-label" x={xCenter(i)} y={M.top + innerH + 20} textAnchor="middle">{lbl}</text>
          ) : null
        )}
        <text className="tr-axis-title" x={M.left + innerW / 2} y={H - 8} textAnchor="middle">REVIEW PERIOD</text>

        {/* index-mode hover columns */}
        {labels.map((_, i) => (
          <rect
            key={`hv${i}`}
            x={M.left + slotW * i}
            y={M.top}
            width={slotW}
            height={innerH}
            fill="transparent"
            onMouseMove={(e) => showTip(e, i)}
            onMouseLeave={hideTip}
          />
        ))}
      </svg>

      {/* bottom legend, point-style like the old usePointStyle:true */}
      <div className="tr-legend">
        {bars.map((b) => (
          <span key={b.label} className="tr-legend-item">
            <span className="tr-sw tr-sw-bar" style={{ background: b.fill, borderColor: b.border }} />
            {b.label}
          </span>
        ))}
        {lines.map((l) => (
          <span key={l.label} className="tr-legend-item">
            <span
              className="tr-sw tr-sw-line"
              style={{ borderColor: l.color, borderTopStyle: l.dash ? (l.dash[0] <= 2 ? "dotted" : "dashed") : "solid" }}
            />
            {l.label}
          </span>
        ))}
      </div>

      <div ref={tooltipRef} className="tr-tooltip" role="tooltip"></div>
    </div>
  );
}
